import React, { useState, useRef } from "react";
import "./contentpage.css";
import LandingSection from "./LandingSection";
import Question from "../components/Question";
import FloatingPageNav from "../components/FloatingPageNav";

function ContemporaryFinance56() {
  const contentRef = useRef(null);
  const titleText = "Impact of Fiat Money on Education";
  const links = {
    prev: "/contemporaryfinance-55",
    index: "/index-finance",
    next: "/contemporaryfinance-57",
  };
  const questions = [
    {
      text: "1. What is the primary consequence of fiat-funded schools according to the lesson?",
      options: [
        {
          value: "A",
          label:
            "A.  They raise the standard of education by offering free access",
        },
        {
          value: "B",
          label: "B. They remain accountable to parents and students",
        },
        {
          value: "C",
          label: "C. They prioritize political goals over genuine education",
        },
        {
          value: "D",
          label: "D. They promote healthy competition among institutions",
        },
      ],
      correct: "C",
    },
    {
      text: "2. Why are private schools considered more accountable than public schools?",
      options: [
        {
          value: "A",
          label: "A.  They receive unlimited funding from central planners",
        },
        {
          value: "B",
          label: "B. Their survival depends on parental satisfaction",
        },
        { value: "C", label: "C. They do not require qualified teachers" },
        { value: "D", label: "D. They avoid competition from other schools" },
      ],
      correct: "B",
    },
    {
      text: "3. What is identified as a major myth about modern universities?",
      options: [
        {
          value: "A",
          label: "A. That they are independent of state financing",
        },
        { value: "B", label: "B. That they receive no research funding" },
        {
          value: "C",
          label: "C. That they only admit students based on merit",
        },
        { value: "D", label: "D. That they never adjust their programs" },
      ],
      correct: "A",
    },
    {
      text: "4. How does fiat financing impact universities compared to a free-market model?",
      options: [
        {
          value: "A",
          label: "A. It allows them to focus solely on students’ needs",
        },
        {
          value: "B",
          label: "B. It disciplines them to improve or face closure",
        },
        {
          value: "C",
          label:
            "C. It makes them more dependent on political and bureaucratic whims",
        },
        { value: "D", label: "D. It ensures lower administrative overhead" },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={56}
        timerMinutes={60}
        currentLevel={4}
        unlockedBadges={7}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 56</b>! In our previous lesson, we explored how fiat
        currency has distorted health and nutrition by promoting harmful
        industrial products over natural and life-sustaining food. Today, we
        turn our focus to a different but equally critical domain: the human
        mind. Education, when functioning properly, is meant to equip
        individuals with the knowledge, skills, and moral grounding necessary to
        flourish in life. Yet when the state, armed with the power of fiat
        money, assumes the role of financier and planner, education is no longer
        about serving students or families. Instead, it is reshaped to serve
        political agendas, bureaucratic expansion, and the perpetuation of
        systems that benefit from dependency and conformity. Schools and
        universities, shielded from the natural discipline of free markets, lack
        accountability to those they are supposed to serve. By examining the
        distortions in both public schooling and higher education, this lesson
        sheds light on how fiat money not only compromises material well-being
        but also erodes the intellectual independence and creativity essential
        to a thriving civilization.
        <br />
        <br />
        <b>Fiat Funding and the Erosion of Accountability in Schools</b>
        <br />
        <br />
        The impact of fiat money on education begins with the earliest stages of
        a child’s schooling. In a healthy society, education is expected to
        serve the needs and aspirations of the student, preparing them for life,
        equipping them with critical thinking, and ensuring they become capable,
        responsible citizens. Yet when education is financed through limitless
        fiat currency controlled by central planners, the incentives that should
        ensure quality and accountability are fundamentally altered. Schools
        funded by fiat no longer need to deliver value to students or parents in
        order to survive. Instead, their survival is guaranteed by government
        budgets and political agendas. This means that education begins to
        reflect not the needs of children but the priorities of those in charge
        of the money printer. During the first decade and a half of a child’s
        life, they are highly dependent on parents and elders to make decisions
        about their well-being. Parents may not always have the training or
        knowledge to fully guide their children’s education, which makes the
        idea of government-supported schooling appealing. However, the
        overlooked truth is that fiat-funded schools are ultimately accountable
        to their funders rather than the children or families they serve. With
        infinite money available to finance these schools, quality no longer
        determines survival. Public schools cannot close their doors no matter
        how poor their performance, and teachers enjoy job security regardless
        of their competence. Unlike private schools that must compete in a
        market where parents withdraw their children if dissatisfied,
        fiat-funded schools operate in an environment stripped of
        accountability. This lack of accountability slowly erodes the quality of
        education, producing generations of students who may spend years in
        classrooms but emerge with minimal critical thinking ability, poor
        literacy and numeracy, and limited preparation for life in a competitive
        world. Infinite fiat money, therefore, becomes not a blessing for
        education but a curse, ensuring mediocrity rather than excellence.
        <br />
        <br />
        <b>Fiat Universities and the Myth of Independence</b>
        <br />
        <br />
        The distortion of education by fiat money does not stop with schools; it
        extends even more profoundly into the university system. Universities
        are often believed to be bastions of independence, intellectual freedom,
        and self-sufficiency, yet in reality most modern universities are deeply
        reliant on government financing. While marketed as private institutions,
        they are sustained by streams of government grants, research funding,
        and above all student loan programs that pump unlimited credit into
        higher education. This creates a profound distortion in incentives.
        Students are no longer the real customers of universities. Instead, they
        become the product that universities sell to governments and corporate
        sponsors. The goal of universities shifts away from providing rigorous
        education and research toward satisfying the demands of bureaucrats,
        politicians, and private interests who control the funding spigot. A
        free market system would keep universities honest: if they failed to
        provide quality education, they would lose students and eventually go
        out of business. Fiat financing, however, shields universities from this
        natural feedback loop. As long as loans continue to flow and subsidies
        are maintained, universities can continue to expand bureaucracies,
        inflate tuition, and lower academic standards without fear of failure.
        This explains why modern campuses are filled with sprawling
        administrative departments, endless deans and coordinators, and
        political programs that often have little connection to genuine academic
        progress. Instead of serving students, universities now serve those who
        fund them, which is made possible only by the infinite fiat printing
        press. The myth of independence disguises the reality that the very
        institutions meant to cultivate free inquiry and intellectual growth are
        increasingly becoming extensions of government planning and political
        agendas.
        <br />
        <br />
        <b>Student Loans, Debt, and the Burden of Fiat Distortion</b>
        <br />
        <br />
        Perhaps the clearest example of fiat’s distortion in higher education is
        the student loan system. In a world of sound money, families and
        students would weigh the costs and benefits of education carefully,
        because resources are scarce and debts must be repaid in hard money that
        holds value over time. Universities would be forced to compete for
        students by offering meaningful, affordable education. The fiat system,
        however, enables governments to create unlimited amounts of credit for
        education. This gives universities a blank check to raise tuition year
        after year, knowing that students will simply borrow more to cover the
        cost. Students are encouraged to take on massive debts before they even
        understand the consequences, saddling themselves with obligations that
        may take decades to repay. Instead of education serving as a foundation
        for independence, it becomes a lifelong financial burden. In many cases,
        the degrees purchased with fiat-funded loans do not yield jobs that
        justify the debt, leaving graduates disillusioned and trapped. This
        distortion also reshapes the structure of universities. Since student
        loans are guaranteed by government fiat, institutions have little
        incentive to control costs, reduce waste, or focus on marketable skills.
        They build luxury campuses, hire layers of administrators, and create
        entire departments devoted to ideological projects rather than practical
        education. This is a direct result of removing market discipline and
        replacing it with unlimited fiat credit. The result is a system where
        students, rather than being the beneficiaries of education, become
        financial instruments in a cycle of debt and dependence. Fiat money, far
        from making education more accessible, has made it more expensive, more
        politicized, and more burdensome to the very people it claims to serve.
        <br />
        <br />
        <b>The Decline of Knowledge and the Rise of Indoctrination</b>
        <br />
        <br />
        The consequences of fiat-funded education reach beyond financial burdens
        and institutional inefficiency; they influence the very content of what
        is taught. In a free system, the competition between schools and
        universities would push them to provide valuable, relevant knowledge
        that helps students succeed in life. With fiat financing, the priorities
        change. Education becomes a tool for those who control the flow of
        money, and curricula begin to reflect political goals rather than the
        pursuit of truth. Public schools may emphasize conformity and obedience
        over independent thought, ensuring that students grow into compliant
        citizens rather than innovative thinkers. Universities, heavily
        dependent on government funding, often shape their research agendas to
        align with political priorities or the interests of corporate sponsors,
        instead of pursuing unbiased scientific inquiry. Students are not merely
        deprived of quality education; they are actively molded into products
        that serve the system sustaining fiat money. The abundance of
        administrators and the growing dominance of ideological programs reflect
        this shift. Instead of cultivating debate, critical thinking, and
        intellectual diversity, fiat-funded education risks becoming a vehicle
        for indoctrination. The deeper tragedy is that generations of students
        may never realize the ways in which their education has been
        compromised. They emerge from schools and universities with diplomas but
        without the intellectual resilience, creativity, or independence
        necessary to challenge the system itself. Fiat money, by distorting
        education, therefore undermines one of the most essential pillars of
        civilization: the cultivation of knowledge and the nurturing of human
        potential.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today's lesson explained how fiat money undermines the integrity of
        education, both at the school and university levels. By giving funders
        unlimited access to money printers, education is no longer accountable
        to students or parents but instead tailored to political agendas and
        bureaucratic interests. Public schools, insulated from market
        discipline, can continue to operate regardless of their declining
        quality, while teachers and administrators face little accountability.
        Universities, though often perceived as private, are deeply dependent on
        subsidies, research grants, and student loans, which transform students
        into products for sponsors rather than beneficiaries of learning. The
        absence of free market pressures allows bureaucrats and political forces
        to dictate priorities, eroding the mission of genuine education. In this
        way, fiat financing corrupts the mind just as it harms the body,
        weakening intellectual development and replacing the pursuit of truth
        with compliance to state-driven narratives.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        As we move forward to Day 57, we will examine how the distortions of
        fiat money extend beyond economics and education into the very heart of
        culture and morality. With central planners wielding the power of
        endless credit, new cultural movements arise that often detach from
        traditional values and long-standing social wisdom. Among the most
        visible consequences is the rise of wokeism, a phenomenon that reflects
        how fiat distortions shape identity, morality, and collective beliefs.
        In the next lesson, we will explore how these trends are not merely
        organic cultural shifts but are deeply tied to the fiat system’s ability
        to sustain ideas and movements that would otherwise struggle to survive
        in a reality governed by natural economic limits.
        <br />
        <br />
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>
      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default ContemporaryFinance56;
